"use strict";
import { jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgentChatbot } from "capitalrx-components-2.0/lib/agent";
import { MarkdownContent } from "capitalrx-components-2.0/lib/components/MarkdownContent";
import { createChatSession, createJarvisFetch, getChatHistory, sendMessageStream } from "capitalrx-jarvis/lib/api";
import { buildAdditionalContext } from "capitalrx-jarvis/lib/context";
import { useAuthToken } from "../hooks";
import { PAGE_AGENT_CHAT_OPT_IN_KEY, PAGE_SNAPSHOT_OPT_IN_KEY, useAgentChatPageContext } from "./AgentChatContext";
import { useHasQueryDevtoolsButton } from "./useHasQueryDevtoolsButton";
import { useAgentChatBaseUrl, useIsAgentChatEnabled } from "./useIsAgentChatEnabled";
const CHAT_SESSION_STORAGE_KEY = "capitalrx.agentChatbot.chatSessionId";
const DISCLAIMER_DISMISSED_STORAGE_KEY = "capitalrx.agentChatbot.disclaimerDismissed";
const TASK_FLYOUT_RESERVED_SPACE = "var(--task_flyout-dimension-collapsed_height, 52px) + var(--task_flyout-spacing-bottom, 1rem)";
const QUERY_DEVTOOLS_RESERVED_SPACE = "56px";
const PERSONA_QUERY_PARAM = "jarvis_persona";
const GENERIC_ERROR_MESSAGE = "Unable to connect to Judi's AI service";
const NO_ATTACHMENTS = [];
export const AgentChatbotFlyout = ({ hasTasks = false }) => {
  const [authToken] = useAuthToken();
  const pageContext = useAgentChatPageContext();
  const [isOpen, setIsOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [toolStatusLabel, setToolStatusLabel] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isDisclaimerDismissed, setIsDisclaimerDismissed] = useState(() => {
    try {
      return window.sessionStorage.getItem(DISCLAIMER_DISMISSED_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });
  const chatSessionIdRef = useRef(null);
  const parentMessageIdRef = useRef(null);
  const lastMessageRef = useRef("");
  const abortRef = useRef(null);
  const entryCountRef = useRef(0);
  const hasLoadedHistoryRef = useRef(false);
  const isMountedRef = useRef(true);
  const baseUrl = useAgentChatBaseUrl();
  const isEnabled = useIsAgentChatEnabled();
  const hasQueryDevtoolsButton = useHasQueryDevtoolsButton();
  const reservedBottomSpace = useMemo(() => {
    const reservations = [];
    if (hasTasks) reservations.push(TASK_FLYOUT_RESERVED_SPACE);
    if (hasQueryDevtoolsButton) reservations.push(QUERY_DEVTOOLS_RESERVED_SPACE);
    if (reservations.length === 0) return void 0;
    return `calc(${reservations.join(" + ")})`;
  }, [hasQueryDevtoolsButton, hasTasks]);
  const personaName = useMemo(() => {
    const value = new URLSearchParams(window.location.search).get(PERSONA_QUERY_PARAM);
    return value != null ? value : void 0;
  }, []);
  const jarvisFetch = useMemo(
    () => createJarvisFetch(baseUrl, authToken != null ? authToken : void 0),
    [authToken, baseUrl]
  );
  const messages = useMemo(() => {
    return entries.map((entry) => {
      if (entry.role === "user") {
        return { id: entry.id, role: entry.role, content: entry.text };
      }
      return { id: entry.id, role: entry.role, content: /* @__PURE__ */ jsx(MarkdownContent, { content: entry.text }) };
    });
  }, [entries]);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      var _a;
      isMountedRef.current = false;
      (_a = abortRef.current) == null ? void 0 : _a.abort();
    };
  }, []);
  const nextEntryId = useCallback((prefix) => {
    entryCountRef.current += 1;
    return `${prefix}-${entryCountRef.current}`;
  }, []);
  const appendEntry = useCallback((entry) => {
    setEntries((previous) => [...previous, entry]);
  }, []);
  const appendToEntry = useCallback((entryId, chunk) => {
    setEntries((previous) => previous.map((entry) => {
      if (entry.id !== entryId) return entry;
      return { ...entry, text: entry.text + chunk };
    }));
  }, []);
  const readStoredSessionId = useCallback(() => {
    try {
      return window.sessionStorage.getItem(CHAT_SESSION_STORAGE_KEY);
    } catch {
      return null;
    }
  }, []);
  const storeSessionId = useCallback((chatSessionId) => {
    try {
      window.sessionStorage.setItem(CHAT_SESSION_STORAGE_KEY, chatSessionId);
    } catch {
    }
  }, []);
  const clearStoredSessionId = useCallback(() => {
    try {
      window.sessionStorage.removeItem(CHAT_SESSION_STORAGE_KEY);
    } catch {
    }
  }, []);
  const ensureSession = useCallback(async () => {
    if (chatSessionIdRef.current) return chatSessionIdRef.current;
    const chatSessionId = await createChatSession({ fetchFn: jarvisFetch, personaName });
    chatSessionIdRef.current = chatSessionId;
    storeSessionId(chatSessionId);
    return chatSessionId;
  }, [jarvisFetch, personaName, storeSessionId]);
  const loadHistory = useCallback(async (chatSessionId) => {
    const history = await getChatHistory({ fetchFn: jarvisFetch, chatSessionId });
    if (!isMountedRef.current || history.messages.length === 0) return;
    entryCountRef.current += history.messages.length;
    setEntries(history.messages.map((message) => ({
      id: message.id,
      role: message.role,
      text: message.content
    })));
    const lastMessage = history.messages[history.messages.length - 1];
    parentMessageIdRef.current = lastMessage ? lastMessage.messageId : null;
  }, [jarvisFetch]);
  useEffect(() => {
    if (!isOpen || hasLoadedHistoryRef.current) return;
    hasLoadedHistoryRef.current = true;
    const storedSessionId = readStoredSessionId();
    if (!storedSessionId) return;
    chatSessionIdRef.current = storedSessionId;
    setIsLoading(true);
    loadHistory(storedSessionId).catch(() => {
      chatSessionIdRef.current = null;
      clearStoredSessionId();
    }).finally(() => {
      if (isMountedRef.current) setIsLoading(false);
    });
  }, [clearStoredSessionId, isOpen, loadHistory, readStoredSessionId]);
  const startAssistantEntry = useCallback((entryId) => {
    setEntries((previous) => {
      const lastEntry = previous[previous.length - 1];
      const withoutPreviousReply = lastEntry && lastEntry.role === "assistant" ? previous.slice(0, -1) : previous;
      return [...withoutPreviousReply, { id: entryId, role: "assistant", text: "" }];
    });
  }, []);
  const dropEmptyAssistantEntry = useCallback(() => {
    setEntries((previous) => {
      const lastEntry = previous[previous.length - 1];
      if (!lastEntry || lastEntry.role !== "assistant" || lastEntry.text) return previous;
      return previous.slice(0, -1);
    });
  }, []);
  const streamAnswer = useCallback(async (message) => {
    var _a, _b;
    const assistantEntryId = nextEntryId("assistant");
    startAssistantEntry(assistantEntryId);
    setIsLoading(true);
    setToolStatusLabel(null);
    setErrorMessage(null);
    const abortController = new AbortController();
    abortRef.current = abortController;
    let bufferedText = "";
    let frameId = null;
    const flushBuffer = () => {
      frameId = null;
      if (!bufferedText) return;
      const chunk = bufferedText;
      bufferedText = "";
      appendToEntry(assistantEntryId, chunk);
    };
    const scheduleFlush = () => {
      if (frameId !== null) return;
      if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
        flushBuffer();
        return;
      }
      frameId = window.requestAnimationFrame(flushBuffer);
    };
    const cancelScheduledFlush = () => {
      if (frameId === null) return;
      if (typeof window !== "undefined" && typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(frameId);
      }
      frameId = null;
    };
    try {
      const chatSessionId = await ensureSession();
      const {
        [PAGE_SNAPSHOT_OPT_IN_KEY]: isPageSnapshotEnabled,
        [PAGE_AGENT_CHAT_OPT_IN_KEY]: _isAgentChatPageEnabled,
        ...publishedContext
      } = pageContext;
      const additionalContext = buildAdditionalContext(
        publishedContext,
        { isPageSnapshotEnabled: isPageSnapshotEnabled === true }
      );
      for await (const packet of sendMessageStream({
        fetchFn: jarvisFetch,
        chatSessionId,
        message,
        parentMessageId: parentMessageIdRef.current,
        signal: abortController.signal,
        additionalContext
      })) {
        if (Object.prototype.hasOwnProperty.call(packet, "reserved_assistant_message_id")) {
          parentMessageIdRef.current = packet["reserved_assistant_message_id"];
        }
        if (Object.prototype.hasOwnProperty.call(packet, "tool_display_name")) {
          const displayName = packet["tool_display_name"];
          setToolStatusLabel(typeof displayName === "string" && displayName ? displayName : null);
        }
        if (Object.prototype.hasOwnProperty.call(packet, "answer_piece")) {
          setToolStatusLabel(null);
          bufferedText += String((_a = packet["answer_piece"]) != null ? _a : "");
          scheduleFlush();
        }
        if (Object.prototype.hasOwnProperty.call(packet, "error")) {
          const detail = (_b = packet["error"]) != null ? _b : packet["message"];
          throw new Error(String(detail || GENERIC_ERROR_MESSAGE));
        }
      }
      cancelScheduledFlush();
      flushBuffer();
    } catch (error) {
      const wasAborted = abortController.signal.aborted;
      abortController.abort();
      cancelScheduledFlush();
      flushBuffer();
      if (!isMountedRef.current || wasAborted) return;
      setErrorMessage(error instanceof Error ? error.message : String(error));
    } finally {
      abortRef.current = null;
      if (isMountedRef.current) {
        setIsLoading(false);
        setToolStatusLabel(null);
      }
    }
  }, [appendToEntry, ensureSession, jarvisFetch, nextEntryId, pageContext, startAssistantEntry]);
  const handleOpen = useCallback(() => setIsOpen(true), []);
  const handleClose = useCallback(() => setIsOpen(false), []);
  const handleDisclaimerDismiss = useCallback(() => {
    setIsDisclaimerDismissed(true);
    try {
      window.sessionStorage.setItem(DISCLAIMER_DISMISSED_STORAGE_KEY, "true");
    } catch {
    }
  }, []);
  const handleClear = useCallback(() => {
    var _a;
    (_a = abortRef.current) == null ? void 0 : _a.abort();
    abortRef.current = null;
    chatSessionIdRef.current = null;
    parentMessageIdRef.current = null;
    lastMessageRef.current = "";
    hasLoadedHistoryRef.current = true;
    clearStoredSessionId();
    setEntries([]);
    setInputValue("");
    setIsLoading(false);
    setToolStatusLabel(null);
    setErrorMessage(null);
    setIsDisclaimerDismissed(false);
    try {
      window.sessionStorage.removeItem(DISCLAIMER_DISMISSED_STORAGE_KEY);
    } catch {
    }
  }, [clearStoredSessionId]);
  const handleSubmit = useCallback(() => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isLoading) return;
    setInputValue("");
    lastMessageRef.current = trimmedInput;
    dropEmptyAssistantEntry();
    appendEntry({ id: nextEntryId("user"), role: "user", text: trimmedInput });
    void streamAnswer(trimmedInput);
  }, [appendEntry, dropEmptyAssistantEntry, inputValue, isLoading, nextEntryId, streamAnswer]);
  const handleRetry = useCallback(() => {
    if (!lastMessageRef.current || isLoading) return;
    setErrorMessage(null);
    void streamAnswer(lastMessageRef.current);
  }, [isLoading, streamAnswer]);
  if (!isEnabled) return null;
  return /* @__PURE__ */ jsx(
    AgentChatbot,
    {
      layout: "standalone",
      dock: "bottom-right",
      reservedBottomSpace,
      open: isOpen,
      onOpen: handleOpen,
      onClose: handleClose,
      onClear: handleClear,
      disclaimer: isDisclaimerDismissed ? null : void 0,
      onDisclaimerDismiss: handleDisclaimerDismiss,
      messages,
      isLoading,
      loadingStatusLabel: toolStatusLabel,
      hasError: Boolean(errorMessage),
      errorMessage: errorMessage != null ? errorMessage : GENERIC_ERROR_MESSAGE,
      onRetry: handleRetry,
      attachedFiles: NO_ATTACHMENTS,
      isAttachmentEnabled: false,
      inputValue,
      onInputChange: setInputValue,
      onSubmit: handleSubmit
    }
  );
};
